//@ts-check
import {
  EDGE_TYPE,
  NODE_TYPE,
  groupEdgeId,
  newGenericEdge,
  newGenericNode,
  newGroupEdge,
  newGroupNode,
} from './reactFlowUtils';

import { extractAddress } from '../../../utils/helperUtils';

/**
 * Prepare Groups
 * @param {Object} prepareGroupParams
 * @param {Object[]} prepareGroupParams.groups list of groups in state
 * @param {String} prepareGroupParams.groupId
 * @returns new Group
 */
export const prepareGroups = ({ groups, groupId }) => {
  let newGroup;
  groups.forEach((g) => {
    if (g.id === groupId) {
      newGroup = g;
    }
  });

  if (!newGroup) {
    newGroup = newGroupNode(groupId);
  }
  return newGroup;
};

/**
 * compute nodes state and address map after new group is created
 * @param {Object} nodeComputeParams
 * @param {Object[]} nodeComputeParams.nodes list of nodes in state
 * @param {String[]} nodeComputeParams.nodeIdsToAdd list of nodeIds to add to the group
 * @param {Object} nodeComputeParams.newGroup group being added to
 * @param {Record<string,Map<string,string>>} nodeComputeParams.addressMap addressMap
 * @returns newNodes, newAddressMap
 */
export const computeNodesWithNewGroup = ({
  nodes,
  nodeIdsToAdd,
  newGroup,
  addressMap,
}) => {
  const newNodes = [newGroup];
  const newAddressMap = {};
  nodes.forEach((n) => {
    if (nodeIdsToAdd.includes(n.id)) {
      if (n.id === nodeIdsToAdd[0]) {
        newGroup.position = n.position;
      }
      newGroup.data.members.push(n);
      const { chain, address } = n.data;
      if (!newAddressMap[chain]) {
        newAddressMap[chain] = new Map(addressMap[chain]);
      }

      newAddressMap[chain].set(extractAddress(address), newGroup.id);
    } else if (newGroup.id !== n.id) {
      newNodes.push(n);
    }
  });
  return { newNodes, newAddressMap };
};

/**
 * adding internal edges to a group, or to a list of member edges
 * @param {Object[]} edgesList reference to list of edges
 * @param {Object} edge
 */
const _addToEdgeList = (edgesList, edge) => {
  if (edge.type === EDGE_TYPE.GROUP) {
    edgesList.push(...edge.data.members);
  } else if (edge.type === EDGE_TYPE.GENERIC) {
    edgesList.push(edge);
  }
};

/**
 * compute nodes state and address map after new group is created
 * @param {Object} edgeComputeParams
 * @param {Object[]} edgeComputeParams.edges list of edges in state
 * @param {String[]} edgeComputeParams.nodeIds list of nodeIds to add to the group
 * @param {Object} edgeComputeParams.newGroup group being added to
 * @returns newEdges
 */
export const computeEdgesWithNewGroup = ({ edges, nodeIds, newGroup }) => {
  const newEdges = {};

  edges.forEach((e) => {
    const source = nodeIds.includes(e.source) ? newGroup.id : e.source;
    const target = nodeIds.includes(e.target) ? newGroup.id : e.target;

    if (
      source === newGroup.id &&
      target === newGroup.id &&
      e.type !== EDGE_TYPE.TEXT
    ) {
      _addToEdgeList(newGroup.data.internalEdges, e);
    } else if (
      (source === newGroup.id || target === newGroup.id) &&
      e.type !== EDGE_TYPE.TEXT
    ) {
      const edgeId = groupEdgeId({
        source,
        target,
      });

      if (!newEdges[edgeId]) {
        newEdges[edgeId] = newGroupEdge({
          source,
          target,
        });
      }
      _addToEdgeList(newEdges[edgeId].data.members, e);
    } else {
      newEdges[e.id] = e;
    }
  });

  return Object.values(newEdges);
};

/**
 * Converts a number in scientific notation to a string of an integer
 * @param {Object} nodeUngroupComputeParams
 * @param {Object[]} nodeUngroupComputeParams.nodes nodes in current state
 * @param {Object[]} nodeUngroupComputeParams.groups groups in current state
 * @param {String} nodeUngroupComputeParams.groupId ID of group being ungrouped
 * @param {Record<string,Map<string,string>>} nodeUngroupComputeParams.addressMap addressMap
 * @returns {{newNodes:Object[], newEdges:Object[], newAddressMap:Record<string,Map<string,string>>, newGroups:Object[]}}
 */
export const computeNodesAfterUngroup = ({
  nodes,
  groups,
  groupId,
  addressMap,
}) => {
  const newNodes = {};
  const newEdges = {};
  /** @type {Record<string,Map<string,string>>} */
  const newAddressMap = {};

  // populate newAddressMap with the current addressMap
  Object.keys(addressMap).forEach((chain) => {
    newAddressMap[chain] = new Map(addressMap[chain]);
  });

  const newGroups = groups.filter((g) => {
    return g.id !== groupId;
  });

  nodes.forEach((n) => {
    if (n.type === NODE_TYPE.GROUP && n.id === groupId) {
      n.data.members.forEach((m) => {
        if (n.id === groupId) {
          // if the node is in the group being ungrouped, create a new node and update the addressMap
          newNodes[m.id] = newGenericNode(m.data);
          newAddressMap[m.data.chain].set(extractAddress(m.data.address), m.id);
        }
      });

      //unpack internalEdges from the group being ungrouped
      if (n.data.internalEdges?.length > 0) {
        n.data.internalEdges.forEach((e) => {
          newEdges[e.id] = e;
        });
      }
    } else {
      newNodes[n.id] = n;
    }
  });

  return {
    newNodes: Object.values(newNodes),
    newAddressMap,
    newGroups,
    newEdges: Object.values(newEdges),
  };
};

/**
 * Converts a number in scientific notation to a string of an integer
 * @param {Object} edgeUngroupComputeParams
 * @param {Object[]} edgeUngroupComputeParams.edges edges in current state
 * @param {Object[]} edgeUngroupComputeParams.newEdgesAfterNodeUpdate edges in updated state
 * @param {Record<string,Map<string,string>>} edgeUngroupComputeParams.newAddressMap addressMap
 * @param {String} edgeUngroupComputeParams.groupId ID of group being ungrouped
 * @returns newEdges
 */
export const computeEdgesAfterUngroup = ({
  edges,
  newEdgesAfterNodeUpdate = [],
  newAddressMap,
  groupId,
}) => {
  const newEdges = newEdgesAfterNodeUpdate.reduce(
    (acc, e) => ({ ...acc, [e.id]: e }),
    {}
  );

  edges.forEach((e) => {
    if (e.source === groupId || e.target === groupId) {
      if (e.type === EDGE_TYPE.GROUP) {
        e.data.members.forEach((m) => {
          // pull chain and addresses from query data of member edges
          const { chain, sourceAddress, targetAddress } =
            m.data.queries[Object.keys(m.data.queries)[0]];

          const sourceNode = newAddressMap[chain].get(
            extractAddress(sourceAddress)
          );
          const targetNode = newAddressMap[chain].get(
            extractAddress(targetAddress)
          );

          if (sourceNode && targetNode) {
            newEdges[m.id] = newGenericEdge({
              ...m.data,
              source: sourceNode,
              target: targetNode,
            });
          }
        });
      }
    } else {
      newEdges[e.id] = e;
    }
  });

  return Object.values(newEdges);
};

const txnQueryKey = (chain, transactionHash) => `${chain}:::${transactionHash}`;

const tokenFlowQueryKey = (chain, tokenAddress, startTime, endTime) =>
  `${chain}:::${extractAddress(tokenAddress)}:::${startTime}:::${endTime}`;

export const dedupingQueryKeyForGroup = ({
  chain,
  transactionHash,
  tokenAddress,
  token,
  startTime,
  endTime,
}) =>
  transactionHash
    ? txnQueryKey(chain, transactionHash)
    : tokenFlowQueryKey(chain, tokenAddress || token, startTime, endTime);
